import React from 'react';
import { Button, Typography, Popconfirm, Spin, Empty, Tag, Modal, message } from 'antd';
import { Link2, Trash2, RefreshCw, ShieldAlert, FlaskConical } from 'lucide-react';
import { useSocialContext } from '../context/social';
import { SocialAccount, SocialProvider } from '../types/social';
import { tokens } from '../theme/tokens';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const IS_MOCK = !process.env.REACT_APP_TIKTOK_AUTH_URL ||
  process.env.REACT_APP_TIKTOK_AUTH_URL.startsWith('http://localhost');

const { Title, Text } = Typography;

const PROVIDER_COLORS: Record<SocialProvider, string> = {
  tiktok: '#010101',
  instagram: '#E1306C',
  google: '#ffffff',
  fake: '#6366f1'
};

const PROVIDER_LABELS: Record<SocialProvider, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  google: 'Google',
  fake: 'FakeSocialMedia'
};

// Inline SVG icons (same as in connect-modal)
const TikTokIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.87a8.18 8.18 0 0 0 4.78 1.52V6.93a4.85 4.85 0 0 1-1.01-.24z' />
  </svg>
);

const InstagramIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' />
  </svg>
);

const GoogleIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24'>
    <path fill='#4285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z' />
    <path fill='#34A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' />
    <path fill='#FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z' />
    <path fill='#EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' />
  </svg>
);

const ICONS: Record<SocialProvider, React.ReactNode> = {
  tiktok: <TikTokIcon />,
  instagram: <InstagramIcon />,
  google: <GoogleIcon />,
  fake: <FlaskConical size={20} />
};

const AccountRow: React.FC<{ account: SocialAccount; onDisconnect: (id: string) => void }> = ({
  account,
  onDisconnect
}) => {
  const connectedDate = new Date(account.connectedAt).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '16px 20px',
        borderRadius: 12,
        background: '#f9fafb',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}
    >
      {/* Provider icon */}
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 10,
          background: PROVIDER_COLORS[account.provider],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          flexShrink: 0
        }}
      >
        {ICONS[account.provider]}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontWeight: 600, color: tokens.colors.primary.neutral[900], fontSize: 14 }}>
            @{account.username}
          </Text>
          <Tag color='blue' style={{ fontSize: 11, padding: '0 6px' }}>
            {PROVIDER_LABELS[account.provider]}
          </Tag>
        </div>
        <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 12 }}>
          Conectado el {connectedDate}
        </Text>
      </div>

      {/* Disconnect */}
      <Popconfirm
        title='Desconectar cuenta'
        description='¿Seguro querés desvincular esta red?'
        onConfirm={() => onDisconnect(account.id)}
        okText='Desconectar'
        cancelText='Cancelar'
        okButtonProps={{ danger: true }}
      >
        <Button
          type='text'
          danger
          icon={<Trash2 size={15} />}
          style={{ color: tokens.colors.supporting.red[500] }}
        />
      </Popconfirm>
    </div>
  );
};

interface ConnectionsPageProps {
  onConnectClick: () => void;
}

export const ConnectionsPage: React.FC<ConnectionsPageProps> = ({ onConnectClick }) => {
  const { accounts, loading, refreshAccounts, disconnectAccount, deleteAllData } = useSocialContext();
  const [messageApi, contextHolder] = message.useMessage();

  const handleDeleteAll = () => {
    Modal.confirm({
      title: 'Eliminar todos mis datos',
      content: 'Esto desvincula todas tus redes y elimina tu sesión local. ¿Confirmás?',
      okText: 'Eliminar todo',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () => {
        deleteAllData();
        messageApi.success('Datos eliminados correctamente.');
      }
    });
  };

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '40px 24px' }}>
      {contextHolder}

      {/* Dev mock banner */}
      {IS_MOCK && (
        <div style={{
          marginBottom: 24, padding: '10px 16px', borderRadius: 8,
          background: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.25)',
          display: 'flex', alignItems: 'center', gap: 8
        }}>
          <span style={{ fontSize: 13, color: '#ca8a04' }}>
            ⚠️ <strong>Modo desarrollo</strong> — Las conexiones son simuladas. Las cuentas que agregues se guardan en localStorage.
          </span>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <Title level={2} style={{ margin: 0, color: tokens.colors.primary.neutral[900], fontWeight: 700 }}>
            Mis redes conectadas
          </Title>
          <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 14, marginTop: 6, display: 'block' }}>
            Administrá las cuentas vinculadas para publicar y ganar puntos.
          </Text>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type='text'
            icon={<RefreshCw size={15} />}
            onClick={refreshAccounts}
            loading={loading}
            style={{ color: tokens.colors.primary.neutral[500] }}
          />
          <Button
            type='primary'
            icon={<Link2 size={15} />}
            onClick={onConnectClick}
            style={{ background: tokens.colors.brand.gradient, border: 'none', fontWeight: 600 }}
          >
            Conectar red
          </Button>
        </div>
      </div>

      {/* Accounts list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size='large' />
        </div>
      ) : accounts.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: tokens.colors.primary.neutral[500] }}>
              Todavía no vinculaste ninguna red
            </span>
          }
        >
          <Button
            type='primary'
            onClick={onConnectClick}
            style={{ background: tokens.colors.brand.gradient, border: 'none', fontWeight: 600 }}
          >
            Conectar mi primera red
          </Button>
        </Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {accounts.map(account => (
            <AccountRow
              key={account.id}
              account={account}
              onDisconnect={disconnectAccount}
            />
          ))}
        </div>
      )}

      {/* Info box */}
      {accounts.length > 0 && (
        <div
          style={{
            marginTop: 32,
            padding: '16px 20px',
            borderRadius: 10,
            background: 'rgba(0,0,0,0.02)',
            border: '1px solid rgba(0,0,0,0.06)'
          }}
        >
          <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 12 }}>
            Las métricas de tus publicaciones se actualizan cada 24hs. Los puntos se acreditan
            automáticamente según el rendimiento de cada video.
          </Text>
        </div>
      )}

      {/* Data deletion */}
      <div style={{ marginTop: 32, paddingTop: 32, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <ShieldAlert size={15} color={tokens.colors.primary.neutral[400]} />
          <Text style={{ color: tokens.colors.primary.neutral[600], fontSize: 13, fontWeight: 600 }}>
            Privacidad y datos
          </Text>
        </div>
        <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 13, display: 'block', marginBottom: 14 }}>
          Podés eliminar todos los datos que Drupi almacena sobre vos. Esto desvincula todas
          las redes conectadas y borra tu sesión local.
        </Text>
        <Button
          size='small'
          danger
          onClick={handleDeleteAll}
          icon={<Trash2 size={13} />}
        >
          Eliminar todos mis datos
        </Button>
      </div>
    </div>
  );
};
